import { ExternalAuthContext, ExternalAuthError } from '@account/account-portal/external-auth'
import {
  logoutUser,
  useLogoutStatus,
  closeAccountPortal,
  useAuthentication,
  setExternalAuthError,
  useRuntimeInfo,
  AUTH_INFO_COOKIE_NAME,
  setAuthContext,
} from '@account/account-portal/lib'
import { LightRegistrationData } from '@account/account-portal/types'
import { Consents } from '@account/consents'
import { ScenarioType, showErrors } from '@account/consents-shared-lib'
import { GlButton } from '@adl/component-button'
import { GlForm } from '@adl/component-form'
import { useDevice, useMfProps, getCookies, setCookie, deleteCookie } from '@crystal/core'
import { Translations, useTranslations } from '@crystal/translator'
import { useEffect } from 'react'

import { useConsents, useUpdateConsents } from '../lib'

import styles from './account-portal.module.scss'
import { ErrorView } from './ErrorView'
import { useFormData } from './landing/account-form'
import { FormContainer } from './layout/FormContainer'

const SCENARIO_TYPE: ScenarioType = 'registration'

export const MembershipConsents = () => {
  const mfProps = useMfProps()
  const { isModalVisible } = useRuntimeInfo()
  const { isMobile } = useDevice()
  const t = useTranslations<Translations>()
  const { handleSubmit } = useFormData<LightRegistrationData>()
  const { authContext } = useAuthentication()
  const userFirstName = (authContext as ExternalAuthContext | undefined)?.authData?.user?.firstName
  const { isPending: isLogoutPending } = useLogoutStatus()
  const consentsState = useConsents(SCENARIO_TYPE)
  const { updateConsents, isUpdatePermissionsPending, consentsApiError, clearApiError } =
    useUpdateConsents(SCENARIO_TYPE)
  const welcomeMessage = userFirstName ? `${t('account.overview.welcome')} ${userFirstName}${t('comma.separator')}` : ''

  useEffect(() => {
    backupAuthInfoCookie()
  }, [])

  async function onSubmit() {
    clearApiError()
    if (!consentsState?.isValid) {
      showErrors(SCENARIO_TYPE)
      return
    } else if (consentsState) {
      await updateConsents()
      restoreAuthInfoCookie()
      setAuthContext({
        ...authContext,
        shouldUpdateConsents: false,
      })

      if (isModalVisible) {
        closeAccountPortal()
      }
    }
  }

  async function onReject() {
    clearApiError()
    deleteBackupAuthInfoCookie()
    await logoutUser()
    setExternalAuthError(
      new ExternalAuthError('Consent update required', {
        visible: false,
      }).serialize(),
    )
    if (isModalVisible) {
      closeAccountPortal()
    }
  }

  return (
    <FormContainer>
      <GlForm noValidate onSubmit={handleSubmit(onSubmit)} className={styles['form-width']}>
        <h3 className={`gl-vspace-bpall-small ${styles.title}`}> {welcomeMessage} </h3>
        <h3 className={styles.title}> {t('consent.screen.title')} </h3>
        <p className={styles['form-title']}> {t('consent.screen.description')} </p>
        <p className={styles.subtitle}> {t('consent.screen.description.details')} </p>
        <Consents {...mfProps} explicit scenarioType={SCENARIO_TYPE} />
        <ErrorView error={consentsApiError} />
        <GlButton
          fullWidth
          type="submit"
          className="gl-vspace-bpall-small"
          aria-label={t('landing.button.label')}
          loading={isUpdatePermissionsPending}
          disabled={isUpdatePermissionsPending}
          id="mem-consents-page-accept-button">
          {t('consent.screen.continue.cta')}
        </GlButton>
        <Consents {...mfProps} implicit scenarioType={SCENARIO_TYPE} />
        <div>
          <GlButton
            fullWidth={isMobile}
            variant="secondary"
            className="gl-vspace-bpall-small"
            aria-label="reject-button"
            onClick={onReject}
            loading={isLogoutPending}
            disabled={isLogoutPending}
            id="mem-consents-page-reject-button">
            {t('consent.screen.decline.cta')}
          </GlButton>
        </div>
      </GlForm>
    </FormContainer>
  )
}

const BACKUP_AUTH_INFO_COOKIE_NAME = `pending__${AUTH_INFO_COOKIE_NAME}`
function backupAuthInfoCookie() {
  const authInfoCookie = getCookies()[AUTH_INFO_COOKIE_NAME]
  if (authInfoCookie) {
    setCookie(BACKUP_AUTH_INFO_COOKIE_NAME, authInfoCookie)
    deleteCookie(AUTH_INFO_COOKIE_NAME)
  }
}

function restoreAuthInfoCookie() {
  const authInfoCookie = getCookies()[BACKUP_AUTH_INFO_COOKIE_NAME]
  if (authInfoCookie) {
    setCookie(AUTH_INFO_COOKIE_NAME, authInfoCookie)
    deleteCookie(BACKUP_AUTH_INFO_COOKIE_NAME)
  }
}
function deleteBackupAuthInfoCookie() {
  deleteCookie(BACKUP_AUTH_INFO_COOKIE_NAME)
}
